import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../client";

const BASE_URL = "shared/Pincodes";

export const getPincodes = createAsyncThunk(
  "Pincodes/list",
  async () => (await api.get(BASE_URL)).data,
);

export const searchPincodes = createAsyncThunk(
  "Pincodes/search",
  async (term) =>
    (await api.get(`${BASE_URL}/search`, { params: { term } })).data,
);

export const savePincodesBulk = createAsyncThunk(
  "Pincodes/bulk",
  async (postOffices) => (await api.post(`${BASE_URL}/bulk`, postOffices)).data,
);

export const getUniquePincodes = createAsyncThunk(
  "Pincodes/unique",
  async () => (await api.get(`${BASE_URL}/unique`)).data,
);

export const getUniqueStates = createAsyncThunk(
  "Pincodes/uniqueStates",
  async () => (await api.get(`${BASE_URL}/unique-states`)).data,
);

export const getUniqueDistricts = createAsyncThunk(
  "Pincodes/uniqueDistricts",
  async () => (await api.get(`${BASE_URL}/unique-districts`)).data,
);

export const getDistrictsByState = createAsyncThunk(
  "Pincodes/districtsByState",
  async (state) =>
    (await api.get(`${BASE_URL}/unique-districts/state/${state}`)).data,
);

export const deletePin = createAsyncThunk(
  "Pincodes/delete",
  async (id) => (await api.delete(`${BASE_URL}/${id}`)).data,
);

export const assignLocalityType = createAsyncThunk(
  "Pincodes/assignLocalityType",
  async ({ pincodes, localityType }) =>
    (
      await api.put(`${BASE_URL}/locality-type/update-by-pincodes`, {
        pincodes,
        localityType,
      })
    ).data,
);

// Fetches post office data via the Fixilo backend, which proxies the India Post API.
// Backend endpoint: GET shared/Pincodes/lookup/{pincode}
// Expected response: { succeeded: true, data: [ { Name, BranchType, District, ... } ] }
export const fetchFromPostalApi = createAsyncThunk(
  "Pincodes/fetchExternal",
  async (pincode, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE_URL}/lookup/${pincode}`);
      if (!data?.data?.length) {
        return rejectWithValue("No post offices found for this pincode.");
      }
      return data.data;
    } catch (err) {
      return rejectWithValue(err ?? "Failed to fetch pincode data.");
    }
  },
);

const addCases = (builder, thunk, onFulfilled) =>
  builder
    .addCase(thunk.pending, (state) => {
      state.isLoading = true;
      state.message = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      onFulfilled(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.message = action.payload ?? action.error.message;
    });

const initialState = {
  list: [],
  uniqueList: [],
  uniqueStates: [],
  uniqueDistricts: [],
  districtsByState: [],
  searchResults: [],
  externalResults: [],
  isLoading: false,
  isSearching: false,
  isFetchingExternal: false,
  isAssigning: false,
  message: null,
};

const slice = createSlice({
  name: "pincodes",
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.searchResults = [];
      state.externalResults = [];
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    addCases(builder, getPincodes, (state, action) => {
      state.list = action.payload.data;
    });
    addCases(builder, getUniquePincodes, (state, action) => {
      state.uniqueList = action.payload.data;
    });
    addCases(builder, getUniqueStates, (state, action) => {
      state.uniqueStates = action.payload.data;
    });
    addCases(builder, getUniqueDistricts, (state, action) => {
      state.uniqueDistricts = action.payload.data;
    });
    addCases(builder, getDistrictsByState, (state, action) => {
      state.districtsByState = action.payload.data;
    });

    builder
      .addCase(searchPincodes.pending, (state) => {
        state.isSearching = true;
        state.message = null;
        state.searchResults = [];
      })
      .addCase(searchPincodes.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload.data ?? [];
      })
      .addCase(searchPincodes.rejected, (state, action) => {
        state.isSearching = false;
        state.message = action.payload ?? action.error.message;
      });

    builder
      .addCase(fetchFromPostalApi.pending, (state) => {
        state.isFetchingExternal = true;
        state.message = null;
        state.externalResults = [];
      })
      .addCase(fetchFromPostalApi.fulfilled, (state, action) => {
        state.isFetchingExternal = false;
        state.externalResults = action.payload;
      })
      .addCase(fetchFromPostalApi.rejected, (state, action) => {
        state.isFetchingExternal = false;
        state.message = action.payload ?? action.error.message;
      });

    addCases(builder, savePincodesBulk, (state, action) => {
      state.message = action.payload.message;
      state.externalResults = [];
    });

    addCases(builder, deletePin, (state, action) => {
      state.message = action.payload.message;
    });

    builder
      .addCase(assignLocalityType.pending, (state) => {
        state.isAssigning = true;
        state.message = null;
      })
      .addCase(assignLocalityType.fulfilled, (state, action) => {
        state.isAssigning = false;
        state.message = action.payload.message;
      })
      .addCase(assignLocalityType.rejected, (state, action) => {
        state.isAssigning = false;
        state.message = action.payload ?? action.error.message;
      });
  },
});

export const { clearSearch } = slice.actions;
export default slice.reducer;
